import { WorkbookExportDialog } from "@/core/components/workbook-export";
import { IcmPagePlanMismatchDialog } from "@/icm/icm-page/icm-page-plan-mismatch-dialog";
import { getAiScriptWorkbookDownloadErrorMessage } from "@/payroll/ai-script/utils";
import { ApprovalRemovalDialog } from "@/payroll/approvals/approval-removal-dialog";
import { AiScriptRerunConfirmationDialog } from "@/payroll/pay-breakdown/ai-script-rerun-confirmation-dialog";
import { PAY_BREAKDOWN_RUN_TARGET_TYPE } from "@/payroll/pay-breakdown/types";
import { PayrollNoteComposer } from "@/payroll/period-notes";
import type { PayBreakdownPageState } from "./use-pay-breakdown-page";

interface PayBreakdownDialogsProps {
  page: PayBreakdownPageState;
}

export const PayBreakdownDialogs = ({ page }: PayBreakdownDialogsProps) => {
  const {
    businessId,
    sheetApprovalActions,
    output,
    selectedPeriod,
    activeAiScriptRuns,
    hasBlockingRun,
    workbookData,
    rerunUnits,
    rerunVersionInfo,
    isDownloadingAiWorkbook,
    isPreparingAiWorkbook,
    aiWorkbookDownloadError,
    isAiWorkbookDownloadComplete,
    handleDownloadAiWorkbook,
    aiScriptRerunConfirmOpen,
    setAiScriptRerunConfirmOpen,
    planPickerOpen,
    setPlanPickerOpen,
    canRerunWholePayroll,
    activePlanId,
    activePlanName,
    lastRunPlanId,
    lastRunPlanName,
    showIcmRerunControls,
    handleTriggerAiRun,
    submitIcmRun,
    isWorkbookExportOpen,
    setIsWorkbookExportOpen,
    noteComposer,
  } = page;

  return (
    <>
      <PayrollNoteComposer composer={noteComposer} />
      {showIcmRerunControls && (
        <IcmPagePlanMismatchDialog
          open={planPickerOpen}
          onOpenChange={setPlanPickerOpen}
          lastRunPlanId={lastRunPlanId}
          lastRunPlanName={lastRunPlanName}
          activePlanId={activePlanId}
          activePlanName={activePlanName}
          onUsePreviousPlan={submitIcmRun}
          onUseCurrentPlan={submitIcmRun}
        />
      )}
      {isWorkbookExportOpen &&
        output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript && (
          <WorkbookExportDialog
            key={`${businessId}-${output.runId}`}
            sheets={workbookData?.spreadsheet.sheets ?? []}
            isDownloading={isDownloadingAiWorkbook}
            isPreparing={isPreparingAiWorkbook}
            isComplete={isAiWorkbookDownloadComplete}
            error={
              aiWorkbookDownloadError
                ? getAiScriptWorkbookDownloadErrorMessage(
                    aiWorkbookDownloadError,
                  )
                : undefined
            }
            onClose={() => setIsWorkbookExportOpen(false)}
            onExport={handleDownloadAiWorkbook}
          />
        )}
      <AiScriptRerunConfirmationDialog
        open={aiScriptRerunConfirmOpen}
        onOpenChange={setAiScriptRerunConfirmOpen}
        onConfirm={handleTriggerAiRun}
        periodStart={selectedPeriod?.period_start}
        periodEnd={selectedPeriod?.period_end}
        versionInfo={rerunVersionInfo}
        units={rerunUnits}
        canRerunEverything={canRerunWholePayroll}
        hasRunInProgress={activeAiScriptRuns.length > 0}
        isBlockedByOtherRun={hasBlockingRun}
      />
      <ApprovalRemovalDialog
        target={sheetApprovalActions.removalTarget}
        isSubmitting={sheetApprovalActions.isPending}
        onOpenChange={sheetApprovalActions.setRemovalOpen}
        onConfirm={sheetApprovalActions.confirmRemoval}
      />
    </>
  );
};
